import { map, first } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { RangeOutput } from '../../_models/common/RangeOutput';
import { UILegend } from '../../_models/common/UILegend';
import { InsectForecast, InsectForecastAggregated } from '../../_models/insects/insectForecast';
import { Measurement } from '../../_models/insects/measurement';
import { CropService } from '../crop.service';
import { environment } from '../../../../environments/environment';
import { InsectsZone } from '../../_models/insects/insectsZone';
import { Insect } from '../../_models/insects/insect';
import { DeviceService } from '../device.service';
import { AlertThreshold, ThresholdLevels } from '../../_models/insects/alertThreshold';
import { insectPhaseColor } from '../../../../assets/data/enum/colorConfiguration';

@Injectable()
export class InsectsZoneService {
  private apiBaseUrl = environment.apiBaseUrl;
  insectsDegreeSelected: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  insectsObjSelected: Subject<Insect> = new Subject<Insect>();
  insectsZoneItemObj: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  listInsectsZone: BehaviorSubject<InsectsZone[]> = new BehaviorSubject<InsectsZone[]>([]);
  listInsects: BehaviorSubject<Insect[]> = new BehaviorSubject<Insect[]>([]);
  insectsInfestationDegree: any[] = [
    {
      id: 1,
      key: 'label_insects_piralide_del_mais',
      insectScientificName: 'Ostrinia nubilalis',
    },
  ];

  LEGEND_DATA_LIST: UILegend[] = [
    {
      id: 0,
      status: 'undefined',
      color: '#000000',
      value: 'diseases.level.undefined',
    },
    {
      id: 1,
      status: 'low',
      color: '#00B050',
      value: 'damageSize.low',
    },
    {
      id: 2,
      status: 'medium_low',
      color: '#92D050',
      value: 'damageSize.medium_low',
    },
    {
      id: 3,
      status: 'medium',
      color: '#FFE699',
      value: 'damageSize.medium',
    },
    {
      id: 4,
      status: 'medium_high',
      color: '#FFC000',
      value: 'damageSize.medium_high',
    },
    {
      id: 5,
      status: 'high',
      color: '#FF0000',
      value: 'damageSize.high',
    },
  ];

  constructor(
    private http: HttpClient,
    private cropService: CropService,
    private deviceService: DeviceService
  ) {
    // this.getInsectsList()
    this.deviceService.getFromLocalStore().subscribe((list) => {
      if (list && list.length > 0) {
        this.getInsectZones();
      }
    });
  }

  initZoneObj() {
    this.insectsZoneItemObj = new BehaviorSubject<any>(null);
  }

  getInsectsObjSelected = () => {
    return this.insectsObjSelected.asObservable();
  };

  getInsectsDegreeSelected = () => {
    return this.insectsDegreeSelected.asObservable();
  };

  setInsectsDegreeSelected = (insectsInfestationDegree) => {
    return this.insectsDegreeSelected.next(insectsInfestationDegree);
  };

  updateInsectsObjSelected(insect: Insect) {
    this.insectsObjSelected.next(insect);
  }

  getInsectsList(): Observable<Insect[]> {
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v2/insects').pipe(
      map((response) => {
        this.listInsects.next(response);
        return response;
      })
    );
  }

  getInsectById = (id: number): Insect => {
    let item: Insect;
    this.listInsects
      .pipe(first((list: Insect[]) => list && list.length > 0))
      .subscribe((list: Insect[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getInsectZones(): Observable<InsectsZone[]> {
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v2/insects/zones').pipe(
      map((response) => {
        const result = this.getElementRequired(response);
        this.listInsectsZone.next(result);
        return result;
      })
    );
  }

  getInsectZoneById(id: number): Observable<InsectsZone> {
    return new Observable((observable) => {
      let zone: InsectsZone = null;
      if (this.listInsectsZone.value.length) {
        zone = this.listInsectsZone.value.find((zone) => zone.id == id);
      }
      observable.next(zone);
      observable.complete();
    });
  }

  getInsectMeasurements(startDate, endDate, insectZoneId): Observable<Measurement[]> {
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v2/insects/measurements' +
          '?insectZoneId=' +
          insectZoneId +
          '&startDate=' +
          encodeURIComponent(start) +
          '&endDate=' +
          encodeURIComponent(end)
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getAggregatedInsectMeasurements(startDate, endDate, insectId): Observable<Measurement[]> {
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v2/insects/measurements/getByInsectAggregated' +
          '?insectId=' +
          insectId +
          '&startDate=' +
          encodeURIComponent(start) +
          '&endDate=' +
          encodeURIComponent(end)
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getInsectForecastsByInsectZone(startDate, endDate, insectZoneId): Observable<InsectForecast[]> {
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    const url =
      this.apiBaseUrl +
      'api/private/v2/insects/forecasts' +
      '?insectZoneId=' +
      insectZoneId +
      '&startDate=' +
      encodeURIComponent(start) +
      '&endDate=' +
      encodeURIComponent(end);

    return this.http.get<any>(url).pipe(map((response) => response));
  }

  getAllInsectForecasts(startDate, endDate): Observable<InsectForecast[]> {
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    const url =
      this.apiBaseUrl +
      'api/private/v2/insects/forecasts' +
      '?startDate=' +
      encodeURIComponent(start) +
      '&endDate=' +
      encodeURIComponent(end);

    return this.http.get<any>(url).pipe(map((response) => response));
  }

  getAverageInsectForecasts(startDate, endDate, insectId): Observable<InsectForecastAggregated[]> {
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v2/insects/forecasts/getAverageGdd' +
          '?insectId=' +
          insectId +
          '&startDate=' +
          encodeURIComponent(start) +
          '&endDate=' +
          encodeURIComponent(end)
      )
      .pipe(map((response) => response));
  }

  getZoneById(id): Observable<InsectsZone> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/insects/zones/' + id)
      .pipe(map((response) => response));
  }

  getAllTypology = () => {
    return ['phero', 'paper', 'note'];
  };

  delete(zone: InsectsZone): Observable<InsectsZone> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v2/insects/zones/' + zone.id).pipe(
      map((response) => {
        if (response) {
          return { id: zone.id } as InsectsZone;
        }
        return null;
      })
    );
  }

  deleteMeasurement(measurement: Measurement): Observable<Measurement> {
    return this.http
      .delete(
        this.apiBaseUrl +
          'api/private/v2/insects/measurements/' +
          measurement.id +
          '?insectZoneId=' +
          measurement.insectZoneId
      )
      .pipe(map(() => ({ id: measurement.id }) as Measurement));
  }

  /**
   * @param zone
   * @returns an insectZone with all associated entities
   */
  addEntities(zone: InsectsZone): InsectsZone {
    if (!zone) {
      return null;
    }

    zone.cropIds = zone.cropIds ?? [];
    zone.crops = zone.cropIds
      .map((id) => this.cropService.getCropAndFieldByCropId(id))
      .filter((z) => !!z);

    zone.insect = this.getInsectById(zone.insectId);
    zone.alertThreshold = this.calculateOtherValueThreshold(
      zone.dailyLowThres,
      zone.dailyHighThres
    );
    zone.weatherStation =
      !zone.weatherStationIsVirtual && zone.weatherStationDid
        ? this.deviceService.getDeviceByCode(zone.weatherStationDid)
        : this.deviceService.getVirtualDevice('xsense');

    zone.sensor =
      !zone.sensorIsVirtual && zone.sensorDid
        ? this.deviceService.getDeviceByCode(zone.sensorDid)
        : this.deviceService.getVirtualDevice('xtrap');

    return zone;
  }

  save(data): Observable<InsectsZone> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    data.companyId = companySelected ? companySelected.id : null;
    data = this.clearInsectZoneToAPI(data);

    if (data.id) {
      return this.http
        .put(this.apiBaseUrl + 'api/private/v2/insects/zones/' + data.id, data)
        .pipe(map((response) => response as InsectsZone));
    } else {
      return this.http
        .post(this.apiBaseUrl + 'api/private/v2/insects/zones', data)
        .pipe(map((response) => response as InsectsZone));
    }
  }

  clearInsectZoneToAPI = (dataItem: InsectsZone) => {
    const data = { ...dataItem };
    delete data.crops;
    delete data.insect;

    if (data.weatherStation) {
      data.weatherStationDid = data.weatherStation.code;
      const idDevice: any = {
        id: data.weatherStation.id,
      };
      data.weatherStation = idDevice;
    }

    if (data.sensor) {
      data.sensorDid = data.sensor.code;
      const idSensor: any = {
        id: data.sensor.id,
      };
      data.sensor = idSensor;
    }

    if (data.alertThreshold) {
      data.dailyLowThres = data.alertThreshold.low;
      data.dailyHighThres = data.alertThreshold.high;
      delete data.alertThreshold;
    }

    return data;
  };

  getElementRequired = (list: Array<InsectsZone>) => {
    const newList: Array<InsectsZone> = [];
    list.forEach((zone: InsectsZone) => {
      newList.push(this.addEntities(zone));
    });
    return newList;
  };

  calculateOtherValueThreshold(min, max): AlertThreshold {
    const alertThreshold = new AlertThreshold();
    if (max > min) {
      const diffDividedByThree = (max - min) / 4;
      alertThreshold.low = min;
      alertThreshold.medium_low = alertThreshold.low + diffDividedByThree;
      alertThreshold.medium = alertThreshold.medium_low + diffDividedByThree;
      alertThreshold.medium_high = alertThreshold.medium + diffDividedByThree;
      alertThreshold.high = max;
    } else {
      alertThreshold.low = min;
      alertThreshold.medium_low = 0;
      alertThreshold.medium = 0;
      alertThreshold.medium_high = 0;
      alertThreshold.high = max;
    }

    return alertThreshold;
  }

  getInsectPhaseColorByPhaseCode(code: string) {
    if (insectPhaseColor.hasOwnProperty(code)) {
      return insectPhaseColor[code];
    }
    return '#FFFFFF';
  }

  uploadImages(file, insectZoneId, date): Observable<any> {
    const imgDate = moment(date).format('DD-MM-YYYY HH:mm:ss');

    return this.http
      .post(
        this.apiBaseUrl +
          'api/private/v2/insects/upload/images' +
          '?zoneId=' +
          insectZoneId +
          '&date=' +
          encodeURIComponent(imgDate),
        file
      )
      .pipe(map((response) => response));
  }

  saveMeasurement(measurement: Measurement) {
    return this.http
      .post(this.apiBaseUrl + 'api/private/v2/insects/measurements', measurement)
      .pipe(map((response) => (response ? response : [])));
  }

  updateMeasurement(measurement: Measurement) {
    return this.http
      .put(this.apiBaseUrl + 'api/private/v2/insects/measurements/' + measurement.id, measurement)
      .pipe(map((response) => (response ? response : [])));
  }

  getLevelByCount(insectZone: InsectsZone, count: number): string {
    const min = insectZone.dailyLowThres ? insectZone.dailyLowThres : 0;
    const max = insectZone.dailyHighThres ? insectZone.dailyHighThres : 0;
    const step = (max - min) / (InsectsZone.LEVELS.length - 1);

    if (count <= min) {
      return ThresholdLevels.LOW;
    }

    if (count >= max) {
      return ThresholdLevels.HIGH;
    }

    let level = ThresholdLevels.UNDEFINED;
    InsectsZone.LEVELS.forEach((lvl: string, index: number) => {
      if (index) {
        const stepMin = step * (index - 1) + min;
        const stepMax = step * index + min;
        if (count > stepMin && count < stepMax) {
          level = lvl;
        }
      }
    });

    return level;
  }

  getInsectZonesMapValues(insect: Insect, timeRange: RangeOutput): Observable<Measurement[]> {
    const start = moment(timeRange.start).startOf('day').toISOString();
    const end = moment(timeRange.end).endOf('day').toISOString();

    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v2/insects/measurements/getByInsect' +
          '?insectId=' +
          insect.id +
          '&startDate=' +
          encodeURIComponent(start) +
          '&endDate=' +
          encodeURIComponent(end)
      )
      .pipe(map((response) => response));
  }
}

// const INSECT_PHASE_KEYS = [
//   "100_1", "100_2", "100_3", "100_4", "100_5", "100_6", "100_7", "100_8", "100_9", "100_10", "100_11", "100_12", "100_13",
//   "111_1", "111_2", "111_3",
//   "114_1", "114_2", "114_3", "114_4",
//   "108_1", "108_2",
//   "104_1", "104_2", "104_3", "104_4", "104_5", "104_6", "104_7", "104_8", "104_9", "104_10", "104_11", "104_12", "104_13", "104_14", "104_15",
//   "103_1", "103_2", "103_3", "103_4", "103_5",
//   "109_1", "109_2", "109_3", "109_4", "109_5",
//   "115_1", "115_2",
//   "117_1", "117_2", "117_3", "117_4",
//   "113_1", "113_2", "113_3", "113_4"
// ]
